import { useState } from 'react';
import { weightFreshness } from '../rules/dosing.js';
import { notify } from '../lib/notify.js';
import { formatDaysAgo } from '../lib/format.js';

// The Calculator's weight check for a child picked in the banner. Silent while
// the weight is fresh — the everyday "Weight: 14.2 kg" line was dropped as
// clutter (it's in Settings) — and shown only once it's past weightFreshness's
// limit, as "still right?", so it can be confirmed or corrected right here, the
// moment it matters, instead of a trip to Settings.
export function ProfileWeight({ darkMode, child, childrenList, saveData, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  if (!child) return null;
  const fresh = weightFreshness(child);
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const link = `font-medium underline ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`;

  const save = (weight) => {
    const w = parseFloat(weight);
    if (!(w > 0 && w < 100)) { notify('Enter a weight in kg, e.g. 14.5'); return; }
    saveData.children(childrenList.map(c =>
      c.id === child.id ? { ...c, weight: w, weightUpdatedAt: new Date().toISOString() } : c));
    setEditing(false);
    onChanged();
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <label htmlFor="profileWeight" className={`text-sm flex-shrink-0 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {child.name}'s weight
        </label>
        <input id="profileWeight" type="number" inputMode="decimal" step="0.1" autoFocus
          value={value} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(value); if (e.key === 'Escape') setEditing(false); }}
          className={`w-20 min-w-0 px-2 py-1 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
            darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'
          }`} />
        <span className={`text-sm ${muted}`}>kg</span>
        <button type="button" onClick={() => save(value)}
          className="px-3 py-1 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white">Save</button>
        <button type="button" onClick={() => setEditing(false)} className={`text-sm ${link}`}>Cancel</button>
      </div>
    );
  }

  if (!fresh.stale) return null;
  const startEdit = () => { setValue(String(child.weight)); setEditing(true); };
  return (
    <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
      ⚠ <span className="font-semibold">{child.weight} kg</span>
      {fresh.known ? `, ${formatDaysAgo(fresh.days)}` : ''} — still right?{' '}
      <button type="button" onClick={() => save(child.weight)} className={link}>Yes</button>
      {' · '}
      <button type="button" onClick={startEdit} className={link}>Update</button>
    </p>
  );
}
